import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Container,
    Button,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Fade,
    Pagination,
} from '@mui/material';
import {
    IconHeart,
    IconSearch,
    IconRotateClockwise2
} from '@tabler/icons-react';
import { colors, fonts } from '../../ColorFont/color';
import { useTranslation } from "react-i18next";
import UnifiedCropCard from '../../components/UnifiedCropCard';
import axios from 'axios';
import { url } from '../../ConstUrl/Url';
import { useNavigate } from 'react-router-dom';
import BlockedView from '../../components/common/BlockedView';
import ContactPopup from "../../components/ViewDetails";
import CropDetailsModal from "../../components/CropDetailsModal";

const Wishlist = ({ userData, setUserData }) => {
    const { t } = useTranslation("wishlist");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    
    const [wishlistItems, setWishlistItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageData, setPageData] = useState(null);
    const [selectedPlot, setSelectedPlot] = useState(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [openPopup, setOpenPopup] = useState(false);

    const fetchWishlist = async () => {
        if (!userData?._id) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await axios.get(`${url}/api/wishlist/${userData._id}?page=${page}`);
            if (response.status === 200) {
                setWishlistItems(response.data.paginatedPlots || []);
                setPageData(response.data);
            }
        } catch (error) {
            console.error("Error fetching wishlist:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWishlist();
    }, [userData?._id, page]);

    useEffect(() => {
        const handleUpdate = () => {
            fetchWishlist();
        };
        window.addEventListener('userDataUpdated', handleUpdate);
        return () => window.removeEventListener('userDataUpdated', handleUpdate);
    }, [userData?._id, page]);

    const handleRefresh = () => {
        setPage(1);
        fetchWishlist();
    };

    const handleOpenDetails = (plot) => {
        setSelectedPlot(plot);
        setIsDetailsModalOpen(true);
    };

    const handleGetContact = (plot) => {
        const userId = userData?._id;
        const plotId = plot?._id;

        if (!userData) {
            const currentUrl = new URL(window.location);
            currentUrl.searchParams.set('openLogin', 'true');
            window.location.href = currentUrl.toString();
            return;
        } else if (userData.noOfContacts <= 0) {
            if (!userData?.purchasedPlots?.some(p => (p.plotId?._id || p.plotId) === plotId)) {
                navigate('/buyer/boost', { state: { cropData: plot } });
                return;
            }
        }

        fetch(`${url}/api/view-plot-details`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId, plotId }),
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.message === "Plot details already viewed") {
                setSelectedPlot(plot);
                setOpenPopup(true);
            } else {
                setUserData(data.user);
                setSelectedPlot(plot);
                setOpenPopup(true);
            }
        })
        .catch((error) => console.error(error));
    };

    if (!userData) {
        return (
            <BlockedView 
                icon={<IconHeart size={48} stroke={1.5} color={colors.primary} />}
                title={t("loginToView")}
                description={t("saveFavDesc")}
                buttonText={t("loginNow")}
                onAction={() => {
                    const currentUrl = new URL(window.location);
                    currentUrl.searchParams.set('openLogin', 'true');
                    window.location.href = currentUrl.toString();
                }}
                sx={{ py: 10 }}
            />
        );
    }

    return (
        <Box sx={{ background: colors.background3, minHeight: '100vh' }}>
            <ContactPopup
                open={openPopup}
                onClose={() => {
                    setOpenPopup(false);
                    setSelectedPlot(null);
                }}
                plot={selectedPlot}
            />

            <CropDetailsModal
                open={isDetailsModalOpen}
                onClose={() => setIsDetailsModalOpen(false)}
                crop={selectedPlot}
                userData={userData}
                onGetContact={handleGetContact}
            />

            <Box sx={{ px: { xs: 2, md: 5 }, py: { xs: 4, md: 6 }, pb: 8 }}>
                {/* Header Area */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
                    <Box>
                        <Typography sx={{ 
                            fontFamily: fonts.poppins, 
                            fontWeight: 800, 
                            fontSize: { xs: '28px', md: '36px' },
                            color: '#1C252E', 
                            letterSpacing: '-1px'
                        }}>
                            {t("myWishlist")}
                        </Typography>
                        <Typography sx={{ 
                            fontFamily: fonts.publicSans, 
                            color: '#637381', 
                            fontSize: '16px',
                            mt: 0.5 
                        }}>
                            {wishlistItems.length} {wishlistItems.length === 1 ? t("itemSaved") : t("itemsSaved")}
                        </Typography>
                    </Box>

                    <Button
                        onClick={handleRefresh}
                        startIcon={<IconRotateClockwise2 size={20} />}
                        sx={{ 
                            color: colors.primary, 
                            fontWeight: 700, 
                            fontFamily: fonts.poppins,
                            textTransform: 'none',
                            '&:hover': { bgcolor: colors.primary_rgba_8 }
                        }}
                    >
                        {t("refresh")}
                    </Button>
                </Box>

                {loading && wishlistItems.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress sx={{ color: colors.primary }} />
                    </Box>
                ) : wishlistItems.length === 0 ? (
                    <Fade in={true}>
                        <Box sx={{ 
                            width: '100%',
                            textAlign: 'center', py: 10, px: 3,
                            bgcolor: '#FFFFFF', borderRadius: '32px',
                            border: '1px dashed #E0E5EB'
                        }}>
                            <Box sx={{ 
                                width: '120px', height: '120px', 
                                bgcolor: '#F4F6F8', borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                mx: 'auto', mb: 3
                            }}>
                                <IconHeart size={48} color="#919EAB" />
                            </Box>
                            <Typography sx={{ fontFamily: fonts.poppins, fontWeight: 700, fontSize: '22px', color: '#1C252E', mb: 1 }}>
                                {t("emptyTitle")}
                            </Typography>
                            <Typography sx={{ fontFamily: fonts.publicSans, color: '#637381', mb: 4, maxWidth: '400px', mx: 'auto' }}>
                                {t("emptyDesc")}
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<IconSearch size={20} />}
                                onClick={() => navigate('/explore')}
                                sx={{
                                    bgcolor: colors.primary,
                                    borderRadius: '12px',
                                    px: 4, py: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    '&:hover': { bgcolor: colors.primary_dark }
                                }}
                            >
                                {t("exploreCrops")}
                            </Button>
                        </Box>
                    </Fade>
                ) : (
                    <Grid container spacing={4} sx={{ mt: 2 }}>
                        {wishlistItems.map((crop, index) => (
                            <Grid item xs={12} sm={6} md={4} key={crop._id} sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Fade in={true} style={{ transitionDelay: `${index * 50}ms`, height: '100%' }}>
                                    <div style={{ height: '100%' }}>
                                        <UnifiedCropCard 
                                            crop={crop} 
                                            userData={userData}
                                            isExplore={true}
                                            onGetContact={handleGetContact}
                                            onDetails={handleOpenDetails}
                                        />
                                    </div>
                                </Fade>
                            </Grid>
                        ))}
                    </Grid>
                )}

                {/* Pagination */}
                {pageData && pageData.pageCount > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                        <Pagination 
                            count={pageData.pageCount}
                            color="primary"
                            page={page}
                            onChange={(e, v) => setPage(v)}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    fontWeight: 700,
                                    borderRadius: '8px',
                                    fontFamily: fonts.poppins
                                }
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Wishlist;
